import { signOut } from "next-auth/react";
import Link from "next/link";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  FiBell,
  FiChevronLeft,
  FiChevronRight,
  FiLogOut,
  FiSettings,
  FiShield,
} from "react-icons/fi";
import {
  ProfileSidebarArrowDownIcon,
  ProfileSidebarArrowUpIcon,
  ProfileSidebarEllipsesIcon,
  UserProfileIcon,
} from "@/assets/svg-icons/svg-icons";
import TextComponent from "@/app/_components/TextComponent";
import cn from "@/utils/cn";
import { useZStore } from "@/utils/z-store";
import { trackGAEvent } from "@/utils/ga";
import { getEffectivePlanType } from "@/utils/subscription-utils";
import { useWorkspaces } from "../_hooks/useWorkspaces";
import WorkspaceSwitcher from "./WorkspaceSwitcher";

// Types
interface Notification {
  _id: string;
  message: string;
  isRead: boolean;
  link?: string;
  createdAt?: string;
}

interface UserProfile {
  userName: string;
  userRole?: string;
  userEmail: string;
  profilePicture?: string | null;
  userImage?: string | null;
  isAdmin?: boolean;
}

type Props = UserProfile;

type DropdownView = "main" | "notifications";

// Map the effective plan type to the short tier chip shown in the header.
const planBadgeLabel = (plan: string | null): string => {
  const p = (plan || "").toUpperCase();
  if (p.includes("TRIAL")) return "Trial";
  if (p.includes("PREMIUM")) return "Premium";
  if (p.includes("STANDARD")) return "Team";
  return "Free";
};

// "Dec 11 2025, 02:43 am"
const formatNotifWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.toLocaleString(undefined, { month: "short" });
  const ampm = d.getHours() >= 12 ? "pm" : "am";
  const hour12 = d.getHours() % 12 || 12;
  const hh = String(hour12).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${month} ${d.getDate()} ${d.getFullYear()}, ${hh}:${mm} ${ampm}`;
};

// Reusable Components
const ProfileAvatar = memo(
  ({
    profilePicture,
    userImage,
    className = "",
    unreadCount = 0,
  }: {
    profilePicture?: string | null;
    userImage?: string | null;
    className?: string;
    unreadCount?: number;
  }) => {
    const imageSrc = profilePicture || userImage;

    return (
      <div className="relative">
        <div
          className={cn(
            "size-[36px] overflow-hidden rounded-full bg-kd-secondary-4 xl:size-[48px]",
            className,
          )}
        >
          {imageSrc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imageSrc}
              alt="Profile"
              className="size-full object-cover"
              loading="lazy"
            />
          ) : (
            <UserProfileIcon className="size-full object-cover text-kd-lte-blue" />
          )}
        </div>
        {unreadCount > 0 && (
          <span className="absolute -right-2 -top-2 flex size-[25px] items-center justify-center rounded-full border-2 border-white bg-[#FA5A7D] text-xs text-white">
            {unreadCount}
          </span>
        )}
      </div>
    );
  },
);

ProfileAvatar.displayName = "ProfileAvatar";

// A single full-width row in the dropdown (Notifications / Settings / Admin /
// Sign out). Renders as a Link when `href` is given, else a button.
const DropdownRow = ({
  icon,
  label,
  href,
  onClick,
  trailing,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  trailing?: React.ReactNode;
}) => {
  const cls =
    "flex w-full items-center gap-3 border-b px-[19px] py-3 text-left transition-colors hover:bg-kd-secondary-6 xl:px-[26px]";
  const inner = (
    <>
      <span
        className="flex w-5 shrink-0 justify-center text-[18px] text-kd-lte-blue"
        aria-hidden="true"
      >
        {icon}
      </span>
      <TextComponent
        type="body"
        className="min-w-0 flex-1 truncate text-kd-secondary-1"
      >
        {label}
      </TextComponent>
      {trailing}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
};

// The back-navigable Notifications sub-view (Image 2).
const NotificationsPanel = memo(
  ({
    notifications,
    onBack,
    onMarkAsRead,
  }: {
    notifications: Notification[];
    onBack: () => void;
    onMarkAsRead: (id: string) => void;
  }) => {
    const list = Array.isArray(notifications) ? notifications : [];

    return (
      <div>
        <header className="flex items-center gap-2 border-b px-[19px] py-4 xl:px-[26px]">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="-ml-1 flex size-6 items-center justify-center rounded transition-colors hover:bg-kd-secondary-6"
          >
            <FiChevronLeft className="size-5 text-kd-secondary-1" />
          </button>
          <TextComponent
            type="h4"
            className="font-semibold text-kd-secondary-1"
          >
            Notifications
          </TextComponent>
        </header>

        <ul className="max-h-[320px] overflow-y-auto py-1" role="list">
          {list.length === 0 ? (
            <li className="px-[19px] py-8 text-center xl:px-[26px]">
              <TextComponent type="caption" className="text-kd-secondary-3">
                No notifications
              </TextComponent>
            </li>
          ) : (
            list.map((n) => {
              const body = (
                <div
                  onClick={() => onMarkAsRead(n._id)}
                  className="flex cursor-pointer items-start gap-3 px-[19px] py-3 transition-colors hover:bg-kd-secondary-6 xl:px-[26px]"
                  role="listitem"
                >
                  <span
                    className={cn(
                      "mt-1.5 inline-block size-2 shrink-0 rounded-full",
                      !n.isRead ? "bg-[#336DFF]" : "bg-gray-300",
                    )}
                    aria-label={n.isRead ? "Read" : "Unread"}
                  />
                  <div className="min-w-0">
                    <TextComponent
                      type="body"
                      className={cn(
                        "break-words",
                        n.link
                          ? "text-[#336DFF] underline"
                          : "text-kd-secondary-1",
                      )}
                    >
                      {n.message}
                    </TextComponent>
                    {n.createdAt && (
                      <TextComponent
                        type="caption"
                        className="mt-0.5 text-kd-secondary-3"
                      >
                        {formatNotifWhen(n.createdAt)}
                      </TextComponent>
                    )}
                  </div>
                </div>
              );

              return n.link ? (
                <li key={n._id}>
                  <Link href={n.link}>{body}</Link>
                </li>
              ) : (
                <li key={n._id}>{body}</li>
              );
            })
          )}
        </ul>
      </div>
    );
  },
);

NotificationsPanel.displayName = "NotificationsPanel";

const UserProfileSidebar = memo(
  ({ userName, userEmail, profilePicture, userImage, isAdmin }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<DropdownView>("main");
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    const sidebarRef = useRef<HTMLDivElement>(null);
    const { actions, data } = useZStore();
    const { activeWorkspace } = useWorkspaces();

    const planLabel = planBadgeLabel(
      getEffectivePlanType(data.subscriptionPlan, data.subscriptionStatus),
    );

    const fetchNotifications = useCallback(async () => {
      try {
        const response = await axios.get("/api/notifications");
        setNotifications(
          Array.isArray(response.data.notifications)
            ? response.data.notifications
            : [],
        );
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        setNotifications([]);
      }
    }, []);

    useEffect(() => {
      fetchNotifications(); // Fetch notifications on component mount

      const intervalId = setInterval(fetchNotifications, 60000); // Every 60s

      return () => {
        clearInterval(intervalId); // Cleanup on component unmount
      };
    }, [fetchNotifications]);

    const markAsRead = useCallback(
      async (notificationId: string) => {
        try {
          await axios.put(`/api/notifications/${notificationId}/read`);
          fetchNotifications(); // Re-fetch notifications
        } catch (error) {
          console.error("Failed to mark notification as read:", error);
        }
      },
      [fetchNotifications],
    );

    const handleToggleMenu = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    // Always reopen the menu on the main view.
    useEffect(() => {
      if (!isOpen) setView("main");
    }, [isOpen]);

    const handleSignOut = useCallback(async () => {
      try {
        trackGAEvent("sign_out", {});
        await signOut({ callbackUrl: "/" });
        actions.resetStore();
        localStorage.clear();
      } catch (error) {
        console.error("Logout error:", error);
      }
    }, [actions]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [isOpen]);

    return (
      <div className="relative" ref={sidebarRef}>
        {/* Trigger Button */}
        <button
          type="button"
          className="flex cursor-pointer items-center justify-center space-x-4 rounded-md"
          onClick={handleToggleMenu}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label="User menu"
        >
          {/* Mobile: 3-dots */}
          <div className="md:hidden">
            <ProfileSidebarEllipsesIcon className="text-kd-lte-blue" />
          </div>

          {/* Desktop: avatar + name */}
          <div className="hidden items-center space-x-3 md:flex">
            <ProfileAvatar
              profilePicture={profilePicture}
              userImage={userImage}
              unreadCount={unreadCount}
            />

            <div className="hidden flex-col md:flex">
              <div className="flex items-center capitalize text-kd-secondary-1">
                <TextComponent type="h4">
                  {userName?.split(" ")[0] || "User"}
                </TextComponent>
                {isOpen ? (
                  <ProfileSidebarArrowUpIcon className="ml-2 size-3 transition-transform xl:h-[7px] xl:w-[13px]" />
                ) : (
                  <ProfileSidebarArrowDownIcon className="ml-2 size-3 transition-transform xl:h-[7px] xl:w-[13px]" />
                )}
              </div>
            </div>
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            role="menu"
            className={cn(
              "absolute right-0 top-16 z-50 select-none overflow-hidden rounded-[12px] border-[0.75px] border-kd-lte-blue bg-white shadow-lg transition-all duration-300 ease-in-out",
              "w-[240px]",
              "xl:w-[300px] xl:rounded-[16px] xl:border",
              "transform scale-100 opacity-100",
            )}
            aria-label="User profile menu"
          >
            {view === "notifications" ? (
              <NotificationsPanel
                notifications={notifications || []}
                onBack={() => setView("main")}
                onMarkAsRead={markAsRead}
              />
            ) : (
              <>
                {/* Header: active workspace + plan tier */}
                <header className="flex items-center gap-2 border-b px-[19px] py-4 xl:px-[26px]">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <TextComponent
                      type="h4"
                      className="truncate font-semibold capitalize text-kd-secondary-1"
                    >
                      {activeWorkspace?.name || userName || "Workspace"}
                    </TextComponent>
                    <TextComponent
                      type="caption"
                      className="mt-0.5 truncate text-kd-secondary-3"
                    >
                      {userEmail}
                    </TextComponent>
                  </div>
                  <span className="shrink-0 rounded-[6px] bg-kd-secondary-6 px-2 py-1 text-[9px] font-semibold text-kd-secondary-2 xl:text-[10px]">
                    {planLabel}
                  </span>
                </header>

                {/* Notifications (opens the sub-panel) */}
                <DropdownRow
                  icon={<FiBell />}
                  label="Notifications"
                  onClick={() => setView("notifications")}
                  trailing={
                    <span className="flex shrink-0 items-center gap-2">
                      {unreadCount > 0 && (
                        <span className="flex min-w-[18px] items-center justify-center rounded-full bg-[#336DFF] px-1.5 text-[10px] font-semibold text-white">
                          {unreadCount}
                        </span>
                      )}
                      <FiChevronRight className="size-4 text-kd-secondary-3" />
                    </span>
                  }
                />

                {/* Switch / Add Workspace */}
                <WorkspaceSwitcher />

                {/* Settings */}
                <DropdownRow
                  icon={<FiSettings />}
                  label="Settings"
                  onClick={() => {
                    window.location.href = "/dashboard/settings/account";
                  }}
                />

                {/* Admin (admins only) */}
                {isAdmin && (
                  <DropdownRow
                    icon={<FiShield />}
                    label="Admin"
                    href="/admin/dashboard"
                  />
                )}

                {/* Sign out */}
                <DropdownRow
                  icon={<FiLogOut />}
                  label="Sign out"
                  onClick={handleSignOut}
                />

                {/* Footer Links */}
                <footer className="pb-4 pl-[19px] pr-[26px] pt-3 xl:pl-[26px]">
                  <TextComponent
                    type="caption"
                    className="flex flex-col text-kd-secondary-3"
                  >
                    <div className="flex space-x-1">
                      <Link
                        href="https://emailauditengine.com/resources/privacy-policy"
                        className="text-kd-secondary-3 underline transition-colors hover:text-gray-700"
                      >
                        Privacy
                      </Link>
                      <span>and</span>
                      <Link
                        href="https://emailauditengine.com/resources/terms-and-conditions"
                        className="text-kd-secondary-3 underline transition-colors hover:text-gray-700"
                      >
                        Terms
                      </Link>
                    </div>
                    <Link
                      href="#"
                      className="mt-1 text-kd-secondary-3 underline transition-colors hover:text-gray-700"
                    >
                      Cookie Preferences
                    </Link>
                  </TextComponent>
                </footer>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

UserProfileSidebar.displayName = "UserProfileSidebar";

export default UserProfileSidebar;
